/**
 * The presentation layout of an org chart, as plain geometry.
 *
 * One function turns a slice of the org into boxes, lines and labels on a
 * 1920 px wide canvas. Everything that shows or exports the chart draws from
 * that same result: the preview (absolutely positioned HTML, which is also what
 * the PNG and PDF are made from) and the PowerPoint file, where every box is a
 * real shape with real text. So the three can never disagree.
 *
 * The shape follows the chart Carl took to his call on 1 Oct 2026: the root's
 * own manager as a dashed context box, the root, then one column per manager
 * who has a team with that team stacked below, and one column for the direct
 * reports without a team.
 */

import type { OrgPerson } from "@/lib/api";
import type { OrgMap } from "@/lib/org";

export const SLIDE_W = 1920;
const MARGIN = 56;
const GAP = 22;
const MAX_COLS = 7;
const CARD_GAP = 7;
const INDENT = 18;

export const BRAND = {
  navy: "#03193C",
  blue: "#115CD3",
  lightBlue: "#E8F0FE",
  card: "#F4F7FC",
  cardLine: "#D9E2F0",
  gold: "#F7C037",
  muted: "#5A6478",
  line: "#9AA6B8",
  subLine: "#C3CFE2",
};

export type BoxKind = "context" | "root" | "manager" | "member" | "label";

export interface SlideBox {
  kind: BoxKind;
  x: number;
  y: number;
  w: number;
  h: number;
  name: string;
  title?: string;
  country?: string;
  moved?: boolean;
}

export interface SlideLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tone: "main" | "context" | "sub";
}

export interface SlideText {
  x: number;
  y: number;
  w: number;
  text: string;
  size: number;
  align: "left" | "right" | "center";
  tone: "muted" | "navy";
  font?: "display";
}

export interface SlideModel {
  width: number;
  height: number;
  boxes: SlideBox[];
  lines: SlideLine[];
  texts: SlideText[];
  people: number;
}

const COUNTRY: Record<string, string> = {
  "united kingdom": "UK",
  "united states": "US",
  usa: "US",
  germany: "DE",
  "czech republic": "CZ",
  czechia: "CZ",
  india: "IN",
  china: "CN",
  denmark: "DK",
  france: "FR",
  spain: "ES",
  italy: "IT",
  mexico: "MX",
  japan: "JP",
  switzerland: "CH",
  brazil: "BR",
  austria: "AT",
  poland: "PL",
  netherlands: "NL",
  sweden: "SE",
};

export function countryCode(country: string | null | undefined): string | undefined {
  if (!country) {
    return undefined;
  }
  const c = country.trim();
  if (c.length <= 3) {
    return c.toUpperCase();
  }
  return COUNTRY[c.toLowerCase()] ?? c.slice(0, 2).toUpperCase();
}

/** "Carl Wood", not the directory's "Wood, Carl". */
export function personName(p: OrgPerson): string {
  if (p.given_name && p.surname) {
    return `${p.given_name} ${p.surname}`;
  }
  const d = p.display_name ?? "";
  const m = /^([^,]+),\s*(.+)$/.exec(d);
  return m ? `${m[2]} ${m[1]}` : d;
}

/** Latin part only: a CJK title half needs fonts a laptop may not have. */
function cleanTitle(t: string | null | undefined): string {
  // Cut at the first CJK character: "IT Director CNEA 中国&东北亚IT总监" keeps
  // "IT Director CNEA", not "IT Director CNEA &IT".
  const cut = (t ?? "").search(/[\u3000-\u9fff\uff00-\uffef]/);
  return (cut >= 0 ? (t ?? "").slice(0, cut) : (t ?? "")).replace(/[\s&/,;:-]+$/, "").trim();
}

/** Rough text height: characters that fit a line at ~7 px each, max 2 lines. */
function titleLines(title: string, width: number, px = 6.9): number {
  if (!title) {
    return 0;
  }
  return Math.min(2, Math.ceil((title.length * px) / Math.max(width - 44, 60)));
}

function cardHeight(title: string, width: number, big = false): number {
  const base = big ? 30 : 28;
  return base + 15 * Math.max(1, titleLines(title, width)) + 10;
}

export interface SlideInput {
  map: OrgMap;
  rootId: string;
  include: (p: OrgPerson) => boolean;
  moved: (id: string) => boolean;
  title: string;
  subtitle: string;
  source: string;
}

export function buildSlide(input: SlideInput): SlideModel {
  const { map, rootId, include, moved } = input;
  const boxes: SlideBox[] = [];
  const lines: SlideLine[] = [];
  const texts: SlideText[] = [];

  const reportsOf = (id: string): OrgPerson[] =>
    [...map.values()]
      .filter(
        (p) => p.manager_id === id && p.id !== id && p.account_enabled !== false && include(p),
      )
      .sort((a, b) =>
        (a.surname ?? a.display_name ?? "").localeCompare(b.surname ?? b.display_name ?? ""),
      );

  // A top with exactly one person below it (Rob Shaw above Carl, once the
  // slide is limited to a scenario's people) is context, not the chart: move
  // down until the root has a real team, so the top becomes the dashed box.
  let start = rootId;
  for (;;) {
    const only = reportsOf(start);
    if (only.length !== 1 || !only[0] || reportsOf(only[0].id).length === 0) {
      break;
    }
    start = only[0].id;
  }
  const root = map.get(start);
  if (!root) {
    return { width: SLIDE_W, height: 400, boxes, lines, texts, people: 0 };
  }

  texts.push({
    x: MARGIN,
    y: 44,
    w: 1400,
    text: input.title,
    size: 40,
    align: "left",
    tone: "navy",
    font: "display",
  });

  let y = 150;
  const cx = SLIDE_W / 2;
  const ctxManager =
    root.manager_id && root.manager_id !== root.id ? map.get(root.manager_id) : undefined;
  if (ctxManager) {
    const w = 300;
    boxes.push({
      kind: "context",
      x: cx - w / 2,
      y,
      w,
      h: 58,
      name: personName(ctxManager),
      title: cleanTitle(ctxManager.job_title),
    });
    lines.push({ x1: cx, y1: y + 58, x2: cx, y2: y + 78, tone: "context" });
    y += 78;
  }
  const rootW = 360;
  boxes.push({
    kind: "root",
    x: cx - rootW / 2,
    y,
    w: rootW,
    h: 78,
    name: personName(root),
    title: cleanTitle(root.job_title),
    country: countryCode(root.country),
    moved: moved(root.id),
  });
  const rootBottom = y + 78;

  const direct = reportsOf(root.id);
  const managers = direct.filter((p) => reportsOf(p.id).length > 0);
  const singles = direct.filter((p) => reportsOf(p.id).length === 0);
  type Col = { head: OrgPerson | null; members: { p: OrgPerson; depth: number }[] };
  const cols: Col[] = managers.map((m) => {
    const members: { p: OrgPerson; depth: number }[] = [];
    const walk = (id: string, depth: number) => {
      for (const r of reportsOf(id)) {
        members.push({ p: r, depth });
        if (depth < 3) {
          walk(r.id, depth + 1);
        }
      }
    };
    walk(m.id, 0);
    return { head: m, members };
  });
  if (singles.length) {
    cols.push({ head: null, members: singles.map((p) => ({ p, depth: 0 })) });
  }

  let people = 1;
  let rowTop = rootBottom + 44;
  let trunkBottom = rootBottom;
  let bottom = rootBottom;
  for (let start = 0; start < cols.length; start += MAX_COLS) {
    const row = cols.slice(start, start + MAX_COLS);
    const n = Math.min(cols.length, MAX_COLS);
    const colW = (SLIDE_W - 2 * MARGIN - (n - 1) * GAP) / n;
    const rowW = row.length * colW + (row.length - 1) * GAP;
    const left = cx - rowW / 2;
    const barY = rowTop - 16;
    const centers = row.map((_, i) => left + i * (colW + GAP) + colW / 2);
    lines.push({ x1: cx, y1: trunkBottom, x2: cx, y2: barY, tone: "main" });
    if (row.length > 1) {
      lines.push({
        x1: centers[0] ?? cx,
        y1: barY,
        x2: centers[centers.length - 1] ?? cx,
        y2: barY,
        tone: "main",
      });
    }
    trunkBottom = barY;
    let rowBottom = rowTop;
    row.forEach((col, i) => {
      const x = left + i * (colW + GAP);
      const ccx = centers[i] ?? x;
      lines.push({ x1: ccx, y1: barY, x2: ccx, y2: rowTop, tone: "main" });
      let cy = rowTop;
      if (col.head) {
        const title = cleanTitle(col.head.job_title);
        const h = cardHeight(title, colW, true);
        boxes.push({
          kind: "manager",
          x,
          y: cy,
          w: colW,
          h,
          name: personName(col.head),
          title,
          country: countryCode(col.head.country),
          moved: moved(col.head.id),
        });
        people += 1;
        cy += h + 10;
      } else {
        boxes.push({
          kind: "label",
          x,
          y: cy,
          w: colW,
          h: 50,
          name: `Further direct reports of ${personName(root).split(" ")[0]}`,
        });
        cy += 60;
      }
      let lastAt: number[] = [];
      for (const { p, depth } of col.members) {
        const ind = Math.min(depth, 2) * INDENT;
        const title = cleanTitle(p.job_title);
        const w = colW - ind;
        const h = cardHeight(title, w);
        if (depth > 0) {
          const parentMid = lastAt[depth - 1] ?? cy;
          lines.push({
            x1: x + ind - 10,
            y1: parentMid,
            x2: x + ind - 10,
            y2: cy + h / 2,
            tone: "sub",
          });
          lines.push({
            x1: x + ind - 10,
            y1: cy + h / 2,
            x2: x + ind,
            y2: cy + h / 2,
            tone: "sub",
          });
        }
        boxes.push({
          kind: "member",
          x: x + ind,
          y: cy,
          w,
          h,
          name: personName(p),
          title,
          country: countryCode(p.country),
          moved: moved(p.id),
        });
        lastAt = lastAt.slice(0, depth);
        lastAt[depth] = cy + h;
        people += 1;
        cy += h + CARD_GAP;
      }
      if (col.head) {
        texts.push({
          x,
          y: cy + 2,
          w: colW,
          text: `${1 + col.members.length} people`,
          size: 12,
          align: "right",
          tone: "muted",
        });
        cy += 20;
      }
      rowBottom = Math.max(rowBottom, cy);
    });
    bottom = rowBottom;
    rowTop = rowBottom + 60;
  }

  texts.push({
    x: MARGIN,
    y: 100,
    w: 1400,
    text: input.subtitle.replace("{n}", String(people)),
    size: 17,
    align: "left",
    tone: "muted",
  });
  const footY = Math.max(bottom + 40, 1000);
  texts.push({
    x: MARGIN + 22,
    y: footY,
    w: 700,
    text: "New reporting line compared with today",
    size: 13,
    align: "left",
    tone: "muted",
  });
  boxes.push({ kind: "member", x: MARGIN, y: footY + 1, w: 15, h: 15, name: "", moved: true });
  texts.push({
    x: SLIDE_W - MARGIN - 1000,
    y: footY,
    w: 1000,
    text: input.source,
    size: 13,
    align: "right",
    tone: "muted",
  });
  return { width: SLIDE_W, height: footY + 46, boxes, lines, texts, people };
}
